import { useState } from "react";
import { Play, FileText, Inbox, FileSignature, CalendarCheck, Receipt, BadgeCheck, Repeat } from "lucide-react";
import { toEmbedUrl } from "../../lib/embed";
import { useBrand } from "../../lib/brand";
import { FadeUp, SectionTag } from "./Reveal";

const FLOW = [
  { icon: Inbox, label: "LEAD" },
  { icon: FileSignature, label: "ESTIMATE" },
  { icon: CalendarCheck, label: "SCHEDULE" },
  { icon: Receipt, label: "INVOICE" },
  { icon: BadgeCheck, label: "PAID ✓" },
  { icon: Repeat, label: "FOLLOW-UP ↗" },
];

export function BrandedPoster({ onPlay, autoplayNote = false }) {
  const brand = useBrand();
  return (
    <div className="relative aspect-video bg-navy blueprint-grid overflow-hidden border border-white/10 grain" data-testid="explainer-fallback">
      <div className="absolute top-4 left-5 flex items-center gap-2.5">
        <span className="w-6 h-6 bg-safety flex flex-col justify-center items-center gap-[2.5px]" aria-hidden="true">
          <span className="block w-3 h-[2px] bg-navy" />
          <span className="block w-2 h-[2px] bg-navy" />
          <span className="block w-1 h-[2px] bg-navy" />
        </span>
        <span className="font-display font-extrabold text-white text-sm sm:text-base tracking-tight">{brand.business_name}</span>
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        {onPlay ? (
          <button onClick={onPlay} data-testid="explainer-play-btn" aria-label="Play explainer video"
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-safety hover:bg-safety-hover grid place-items-center transition-all hover:scale-105 shadow-[0_0_50px_rgba(255,85,0,0.35)]">
            <Play size={30} className="text-white ml-1" fill="white" />
          </button>
        ) : (
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-safety grid place-items-center shadow-[0_0_50px_rgba(255,85,0,0.35)]">
            <Play size={30} className="text-white ml-1" fill="white" />
          </div>
        )}
        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-slate-400 mt-5 text-center">
          {autoplayNote ? "Video coming soon" : "Watch the 90-second explainer"}
        </p>
      </div>
      <div className="absolute bottom-12 sm:bottom-14 inset-x-0 flex justify-center gap-1.5 sm:gap-2 px-4">
        {FLOW.map((f, i) => (
          <div key={f.label} className="flex items-center gap-1.5 sm:gap-2">
            <div className="bg-charcoal/90 border border-white/15 px-2 sm:px-2.5 py-1.5 flex items-center gap-1.5">
              <f.icon size={12} className="text-safety" />
              <span className="font-mono text-[8px] sm:text-[9px] tracking-wider text-slate-300">{f.label}</span>
            </div>
            {i < FLOW.length - 1 && <span className="text-safety text-[10px]">→</span>}
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 inset-x-0 bg-charcoal/80 border-t border-white/10 px-4 py-2.5 text-center">
        <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-slate-300">
          The Back Office Blueprint — For businesses that do the work
        </p>
      </div>
    </div>
  );
}

export function WorkflowBoard() {
  const rows = [
    { icon: Inbox, tag: "LEAD", text: "New lead — website form", meta: "auto-reply sent · 8s", accent: false },
    { icon: FileSignature, tag: "ESTIMATE", text: "EST-0143 sent", meta: "quoted in 42 min", accent: false },
    { icon: CalendarCheck, tag: "SCHEDULE", text: "Thu 10:30 confirmed", meta: "reminder queued", accent: false },
    { icon: Receipt, tag: "INVOICE", text: "INV-0389 sent on-site", meta: "4:12 PM", accent: false },
    { icon: BadgeCheck, tag: "PAID", text: "$630 — card", meta: "job to cash · 1h 51m", accent: true },
    { icon: Repeat, tag: "FOLLOW-UP", text: "review request out", meta: "rebook in 2 wks", accent: false },
  ];
  return (
    <div className="relative" data-testid="hero-workflow-board">
      <div className="absolute -top-4 -left-4 w-full h-full border border-safety/40" />
      <div className="relative bg-charcoal border border-white/10 p-5 sm:p-6 grain">
        <div className="flex justify-between items-center mb-5 pb-4 border-b border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-safety">[TODAY'S BOARD]</p>
          <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">all systems green</p>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <FadeUp key={r.tag} delay={0.9 + i * 0.12}>
              <div className={`flex items-center gap-3.5 px-4 py-3 border ${r.accent ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-navy"}`}>
                <r.icon size={16} className={r.accent ? "text-emerald-400" : "text-safety"} />
                <span className="font-mono text-[9px] tracking-[0.18em] text-slate-500 w-20 shrink-0">{r.tag}</span>
                <span className="text-white text-sm flex-1">{r.text}</span>
                <span className="font-mono text-[10px] text-slate-500 hidden sm:block">{r.meta}</span>
              </div>
            </FadeUp>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-xs text-slate-400">jobs confirmed, invoiced & followed up — before 5 PM</p>
          <p className="font-display font-extrabold text-white text-2xl">7<span className="text-safety">/7</span></p>
        </div>
      </div>
    </div>
  );
}

export default function ExplainerSection() {
  const brand = useBrand();
  const [playing, setPlaying] = useState(false);
  const ex = brand.explainer || {};
  const embed = toEmbedUrl(ex.video_url);
  const isDirectFile = ex.video_url && !embed && /\.(mp4|webm|mov)(\?|$)/i.test(ex.video_url);
  const autoplay = !!ex.autoplay;

  return (
    <section className="bg-bone relative blueprint-grid-light" data-testid="explainer-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-24">
        <FadeUp className="text-center max-w-2xl mx-auto mb-12">
          <SectionTag>What Is {brand.business_name}?</SectionTag>
          <h2 className="font-display font-extrabold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
            Build the business behind the work.
          </h2>
        </FadeUp>

        <FadeUp delay={0.15}>
          {!ex.video_url && <BrandedPoster autoplayNote />}
          {ex.video_url && !playing && !autoplay && (
            <div className="relative">
              {ex.thumbnail_url ? (
                <div className="relative aspect-video bg-navy border border-white/10 overflow-hidden">
                  <img src={ex.thumbnail_url} alt="Explainer video thumbnail" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 grid place-items-center bg-navy/40">
                    <button onClick={() => setPlaying(true)} data-testid="explainer-play-btn" aria-label="Play explainer video"
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-safety hover:bg-safety-hover grid place-items-center transition-all hover:scale-105">
                      <Play size={30} className="text-white ml-1" fill="white" />
                    </button>
                  </div>
                </div>
              ) : (
                <BrandedPoster onPlay={() => setPlaying(true)} />
              )}
            </div>
          )}
          {ex.video_url && (playing || autoplay) && embed && (
            <div className="aspect-video bg-black border border-white/10" data-testid="explainer-player">
              <iframe
                src={`${embed}${embed.includes("?") ? "&" : "?"}${autoplay ? "autoplay=1&mute=1" : "autoplay=1"}`}
                title={`${brand.business_name} explainer video`}
                className="w-full h-full" allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            </div>
          )}
          {ex.video_url && (playing || autoplay) && !embed && (
            <video
              data-testid="explainer-player"
              className="w-full aspect-video bg-black border border-white/10"
              src={ex.video_url}
              poster={ex.thumbnail_url || undefined}
              controls autoPlay={autoplay} muted={autoplay} playsInline loop={autoplay}
            >
              {ex.captions_url && (
                <track kind="captions" src={ex.captions_url} srcLang="en" label="English captions" default />
              )}
            </video>
          )}
          {isDirectFile && !playing && !autoplay && null}
        </FadeUp>

        {ex.transcript && (
          <FadeUp delay={0.25} className="mt-6">
            <details className="bg-white border border-slate-200 group" data-testid="explainer-transcript">
              <summary className="px-5 py-4 cursor-pointer flex items-center gap-3 text-navy font-semibold text-sm select-none">
                <FileText size={16} className="text-safety" />
                Read the transcript
                <span className="ml-auto font-mono text-[10px] text-slate-400 uppercase tracking-widest group-open:hidden">expand</span>
              </summary>
              <p className="px-5 pb-5 pt-1 text-slate-600 text-sm leading-relaxed whitespace-pre-line">{ex.transcript}</p>
            </details>
          </FadeUp>
        )}
      </div>
    </section>
  );
}
